/**
 * Unified listing shippingRates: default + destination zones.
 *
 * Dual-read prefers publicData.shippingRates; otherwise legacy flat fee fields
 * become default with no zones. Writes elsewhere must mirror default back onto
 * shippingPriceInSubunitsOneItem / AdditionalItems.
 *
 * Resolve precedence: US state > country > default.
 */

import { isUsStateCode, toCountryCode, toUsStateCode } from '../config/usStates';

const toSubunits = value => {
  const amount =
    typeof value === 'number'
      ? value
      : value && typeof value.amount === 'number'
      ? value.amount
      : null;
  return typeof amount === 'number' && Number.isInteger(amount) && amount >= 0 ? amount : 0;
};

const isCountryCode = code => typeof code === 'string' && /^[A-Z]{2}$/.test(code);

const feePair = (source = {}) => ({
  shippingPriceInSubunitsOneItem: toSubunits(source.shippingPriceInSubunitsOneItem),
  shippingPriceInSubunitsAdditionalItems: toSubunits(source.shippingPriceInSubunitsAdditionalItems),
});

const normalizeZone = zone => ({
  name: typeof zone?.name === 'string' ? zone.name : '',
  states: Array.isArray(zone?.states) ? zone.states.filter(code => typeof code === 'string') : [],
  countries: Array.isArray(zone?.countries)
    ? zone.countries.filter(code => typeof code === 'string')
    : [],
  ...feePair(zone),
});

const hasRatesShape = value =>
  !!value && typeof value === 'object' && value.default && typeof value.default === 'object';

const asRates = ratesOrPublicData => {
  if (hasRatesShape(ratesOrPublicData)) {
    return {
      default: feePair(ratesOrPublicData.default),
      zones: Array.isArray(ratesOrPublicData.zones)
        ? ratesOrPublicData.zones.map(normalizeZone)
        : [],
    };
  }
  return normalizeShippingRates(ratesOrPublicData);
};

/**
 * @param {Object|null|undefined} publicData listing publicData
 * @returns {{
 *   default: { shippingPriceInSubunitsOneItem: number, shippingPriceInSubunitsAdditionalItems: number },
 *   zones: Array<Object>
 * }}
 */
export const normalizeShippingRates = publicData => {
  if (hasRatesShape(publicData?.shippingRates)) {
    return asRates(publicData.shippingRates);
  }
  return {
    default: feePair(publicData),
    zones: [],
  };
};

/**
 * @param {Array<Object>|null|undefined} zones
 * @returns {{ valid: boolean, errors: Array<{ index: number, code: string }> }}
 */
export const validateShippingZones = zones => {
  if (!Array.isArray(zones) || zones.length === 0) {
    return { valid: true, errors: [] };
  }

  const errors = [];
  const seenStates = new Set();
  const seenCountries = new Set();

  zones.forEach((zone, index) => {
    const states = Array.isArray(zone?.states) ? zone.states : [];
    const countries = Array.isArray(zone?.countries) ? zone.countries : [];

    if (states.length === 0 && countries.length === 0) {
      errors.push({ index, code: 'empty_members' });
    }

    const first = zone?.shippingPriceInSubunitsOneItem;
    if (!(typeof first === 'number' && Number.isInteger(first) && first >= 0)) {
      errors.push({ index, code: 'missing_fees' });
    }

    states.forEach(code => {
      if (!isUsStateCode(code)) {
        errors.push({ index, code: 'invalid_code' });
        return;
      }
      if (seenStates.has(code)) {
        errors.push({ index, code: 'overlap_state' });
        return;
      }
      seenStates.add(code);
    });

    countries.forEach(code => {
      if (!isCountryCode(code)) {
        errors.push({ index, code: 'invalid_code' });
        return;
      }
      if (seenCountries.has(code)) {
        errors.push({ index, code: 'overlap_country' });
        return;
      }
      seenCountries.add(code);
    });
  });

  return { valid: errors.length === 0, errors };
};

/**
 * @param {Object} ratesOrPublicData shippingRates or listing publicData
 * @param {{ country?: string, state?: string }} [destination]
 * @returns {{
 *   shippingPriceInSubunitsOneItem: number,
 *   shippingPriceInSubunitsAdditionalItems: number,
 *   matched: 'state'|'country'|'default'
 * }}
 */
export const resolveShippingFees = (ratesOrPublicData, destination = {}) => {
  const rates = asRates(ratesOrPublicData);
  const rawCountry = typeof destination.country === 'string' ? destination.country.trim() : '';
  const rawState = typeof destination.state === 'string' ? destination.state.trim() : '';
  const country = toCountryCode(rawCountry) || rawCountry || null;
  const treatAsUs = country === 'US' || !country;
  const state = (treatAsUs ? toUsStateCode(rawState) || rawState : rawState) || null;

  if (treatAsUs && state) {
    const zone = rates.zones.find(z => z.states.includes(state));
    if (zone) {
      return { ...feePair(zone), matched: 'state' };
    }
  }

  if (country) {
    const zone = rates.zones.find(z => z.countries.includes(country));
    if (zone) {
      return { ...feePair(zone), matched: 'country' };
    }
  }

  return { ...feePair(rates.default), matched: 'default' };
};

/**
 * @param {Object} ratesOrPublicData shippingRates or listing publicData
 * @returns {{ min: number, max: number }}
 */
export const shippingFirstItemRange = ratesOrPublicData => {
  const rates = asRates(ratesOrPublicData);
  const firsts = [
    rates.default.shippingPriceInSubunitsOneItem,
    ...rates.zones.map(zone => zone.shippingPriceInSubunitsOneItem),
  ];
  return { min: Math.min(...firsts), max: Math.max(...firsts) };
};

/**
 * @param {Object|null|undefined} shippingRates
 * @returns {{ shippingPriceInSubunitsOneItem: number, shippingPriceInSubunitsAdditionalItems: number }}
 */
export const mirrorDefaultToLegacyFlat = shippingRates => feePair(shippingRates?.default);

const moneyAmount = value => {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }
  if (value && typeof value.amount === 'number' && Number.isInteger(value.amount)) {
    return value.amount;
  }
  return null;
};

/**
 * Form-friendly read of listing shipping fees. Prefers shippingRates; otherwise
 * uses legacy flats. Unlike normalizeShippingRates, missing amounts stay null
 * so empty currency fields are not prefilled as $0.00.
 *
 * @param {Object|null|undefined} publicData listing publicData
 * @returns {{
 *   default: { shippingPriceInSubunitsOneItem: number|null, shippingPriceInSubunitsAdditionalItems: number|null },
 *   zones: Array<Object>
 * }}
 */
export const initialShippingRatesFromPublicData = publicData => {
  if (hasRatesShape(publicData?.shippingRates)) {
    const rates = asRates(publicData.shippingRates);
    const sourceDefault = publicData.shippingRates.default || {};
    return {
      default: {
        shippingPriceInSubunitsOneItem:
          typeof sourceDefault.shippingPriceInSubunitsOneItem === 'number'
            ? sourceDefault.shippingPriceInSubunitsOneItem
            : null,
        shippingPriceInSubunitsAdditionalItems:
          typeof sourceDefault.shippingPriceInSubunitsAdditionalItems === 'number'
            ? sourceDefault.shippingPriceInSubunitsAdditionalItems
            : null,
      },
      zones: rates.zones,
    };
  }

  return {
    default: {
      shippingPriceInSubunitsOneItem:
        typeof publicData?.shippingPriceInSubunitsOneItem === 'number'
          ? publicData.shippingPriceInSubunitsOneItem
          : null,
      shippingPriceInSubunitsAdditionalItems:
        typeof publicData?.shippingPriceInSubunitsAdditionalItems === 'number'
          ? publicData.shippingPriceInSubunitsAdditionalItems
          : null,
    },
    zones: [],
  };
};

/**
 * Map Edit Listing delivery form values onto publicData shipping fields.
 * Mirrors shippingRates.default onto the legacy flat fee keys.
 *
 * @param {Object} values delivery form values
 * @param {{ showAdditionalShippingFee?: boolean, allowZones?: boolean }} [options]
 * @returns {Object} publicData shipping slice
 */
export const buildShippingPublicDataFromDeliveryValues = (
  values = {},
  { showAdditionalShippingFee, allowZones = true } = {}
) => {
  const deliveryOptions = Array.isArray(values.deliveryOptions) ? values.deliveryOptions : [];
  const shippingEnabled = deliveryOptions.includes('shipping');
  const pickupEnabled = deliveryOptions.includes('pickup');

  if (!shippingEnabled) {
    return { shippingEnabled: false, pickupEnabled };
  }

  const first = moneyAmount(values.shippingPriceInSubunitsOneItem);
  const additional = showAdditionalShippingFee
    ? moneyAmount(values.shippingPriceInSubunitsAdditionalItems)
    : null;
  const defaultFees = {
    shippingPriceInSubunitsOneItem: toSubunits(first),
    shippingPriceInSubunitsAdditionalItems: showAdditionalShippingFee
      ? toSubunits(additional)
      : null,
  };
  const shippingRates = {
    default: defaultFees,
    zones:
      allowZones !== false && Array.isArray(values.zones) ? values.zones.map(normalizeZone) : [],
  };

  return {
    shippingEnabled: true,
    pickupEnabled,
    shippingRates,
    shippingPriceInSubunitsOneItem: defaultFees.shippingPriceInSubunitsOneItem,
    shippingPriceInSubunitsAdditionalItems: defaultFees.shippingPriceInSubunitsAdditionalItems,
  };
};
